<template>
    <div class="container mt-5">
        <div class="row">
            <div class="col-12 col-md-10 col-lg-6">
                <h1 class="h5 mb-3 font-weight-bolder">
                    <span class="icon icon-glyphicons-basic-cogwheel"></span>
                    Workspace Settings
                </h1>
                <p class="text-secondary">Manage global settings for your workspace.</p>
                <hr />
                <div class="pb-2">
                    <span class="mr-2 align-middle">Current Plan:</span>
                    <span class="badge badge-highlight badge-large mr-3 align-middle">{{ currentPricingTierName }}</span>
                    <a
                        v-if="organizationBillingEnabled"
                        href="#"
                        class="small align-middle"
                        @click="$refs.billingView.open()"
                    >
                        <span style="margin-right: 2px">Manage Plan</span>
                        <span class="icon icon-100 icon-glyphicons-basic-square-new-window"></span>
                    </a>
                </div>
                <p class="font-weight-500 text-secondary small mb-3 mt-1">Workspace ID: 00{{ organization.id }}</p>
                <base-form v-model="organization" :fields="settingsFields"></base-form>
                <div v-if="customBrandingEnabled">
                    <p class="mb-1 mt-4">Organization Logo</p>
                    <p class="mb-3 text-secondary small">
                        Upload an .png or .jpg of your organization's logo. Once uploaded, Share Links and Learner
                        Results PDF will be branded with your logo.
                    </p>
                    <file-upload
                        v-model="organization.logo_image"
                        placeholder="Drop an image here"
                        :replaceable="true"
                        :max-bytes="1000000"
                        accept=".png|.jpg"
                        :warning-bytes="500000"
                        file-type-label="image"
                        :direct-upload="true"
                    ></file-upload>
                </div>
                <div v-else-if="organizationBillingEnabled">
                    <p class="text-secondary">
                        To enable custom branding,
                        <a href="#" @click="$refs.billingView.open()">upgrade your plan</a>.
                    </p>
                </div>
                <div class="d-flex mt-4">
                    <div class="flex-grow-0">
                        <button @click="save()" class="btn btn-sm btn-primary mr-2" :disabled="loading">Save</button>
                        <button v-if="freeTierActive" @click="openDeactivate()" class="btn btn-sm btn-gray">
                            Deactivate Workspace
                        </button>
                    </div>
                </div>
                <br />
            </div>
        </div>
        <base-modal id="deactivate-workspace" :centered="true" :loading="modalLoading">
            <template slot="title">
                <span>Deactivate Workspace</span>
            </template>
            <template>
                <p class="mt-2">
                    <span class="icon icon-glyphicons-basic-circle-empty-alert align-middle mr-1"></span>
                    <span class="align-middle"
                        >Once deactivated, access to all scenarios in this workspace will be lost.</span
                    >
                </p>
                <p class="mt-2 text-secondary">
                    Deactivate this workspace by typing the word 'DEACTIVATE' in the space below.
                </p>
                <input v-model="deactivateTextEntry" type="text" class="form-control" placeholder="DEACTIVATE" />
            </template>
            <template slot="footer">
                <button class="btn btn-sm btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="deactivate()"
                    class="btn btn-sm btn-danger"
                    :disabled="modalLoading || deactivateBtnDisabled"
                >
                    Deactivate
                </button>
            </template>
        </base-modal>
        <billing-view ref="billingView"></billing-view>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import FileUpload from "@/components/FileUpload"
import BillingView from "@/components/BillingView"
import BaseModal from "@/components/BaseModal"
import { timezones } from "@/assets/js/timezones.js"

export default {
    name: "OrganizationDetails",
    components: {
        BaseForm,
        FileUpload,
        BillingView,
        BaseModal,
    },
    data() {
        return {
            organization: {},
            timezones: timezones,
            modalLoading: false,
            deactivateTextEntry: "",
        }
    },
    computed: {
        currentOrganizationID() {
            return this.$store.getters.currentOrganizationId
        },
        organizationDetails() {
            return this.$store.getters.organizationDetails
        },
        organizationBillingEnabled() {
            return this.$store.getters.organizationBillingEnabled
        },
        customBrandingEnabled() {
            if (!this.organizationDetails) return false
            return this.organizationDetails.ff_custom_branding
        },
        loading() {
            return this.$store.getters.loading
        },
        freeTierActive() {
            return this.$store.getters.freeTierActive
        },
        currentPricingTierName() {
            return this.$store.getters.currentPricingTierName
        },
        settingsFields() {
            let vm = this
            return [
                {
                    name: "Basic Info",
                    type: "divider",
                },
                {
                    name: "Workspace Identifier",
                    placeholder: "Organization Name",
                    key: "name",
                    subtext: "A short name used to identify your workspace in the workspace dropdown menu.",
                    required: true,
                    maxlength: 40,
                    showLimit: true,
                },
                {
                    name: "Full Workspace Name",
                    placeholder: "Organization Name",
                    key: "public_name",
                    subtext: "Your organization or team's formal name.",
                    required: true,
                    maxlength: 300,
                },

                {
                    name: "Main Timezone",
                    key: "hq_timezone_name",
                    subtext: "Your organization's main timezone.",
                    type: "select",
                    options: timezones.map((t) => {
                        return {
                            value: t,
                            label: t,
                        }
                    }),
                },
                {
                    name: "Custom Branding",
                    type: "divider",
                    key: "customBranding",
                    hidden() {
                        return !vm.customBrandingEnabled
                    },
                },
            ]
        },
        deactivateBtnDisabled() {
            return this.deactivateTextEntry != "DEACTIVATE"
        },
    },
    methods: {
        async fetchData() {
            this.$store.dispatch("addLoad")
            if (!this.currentOrganizationID) {
                await this.$store.dispatch("fetchAuth")
            }
            let vm = this
            http.get(`/api/organizations/${this.currentOrganizationID}/settings/`).then(function (response) {
                vm.organization = response.data
                vm.$store.dispatch("removeLoad")
            })
        },
        save() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.put(`/api/organizations/${this.currentOrganizationID}/settings/`, {
                name: this.organization.name,
                public_name: this.organization.public_name,
                logo_image: this.organization.logo_image,
                hq_timezone_name: this.organization.hq_timezone_name,
            }).then(function (response) {
                vm.$store.dispatch("removeLoad")
                vm.$notify({
                    group: "main",
                    type: "success",
                    text: "Settings saved.",
                    data: {},
                })
                // Fetch authentication data so that the topnav reflects the updated
                // organization name.
                vm.$store.dispatch("fetchAuth")
            })
        },
        openDeactivate() {
            $("#deactivate-workspace").modal("show")
        },
        deactivate() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/organizations/${this.currentOrganizationID}/deactivate/`)
                .then(function () {
                    window.location = "/"
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>
